import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { forgotPassword, resetPassword } from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import './Auth.css';

/** Normalize FastAPI error detail */
const getApiError = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
    }
    return fallback;
};

/**
 * Handles both stages of password reset:
 *   1. No token in URL -> show "enter your email" form (forgot-password request)
 *   2. Token in URL    -> show "enter new password" form (actual reset)
 */
const ResetPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    // ── Forgot-password (request email) state ──
    const [email, setEmail] = useState('');
    const [requestSent, setRequestSent] = useState(false);

    // ── Reset-password (set new password) state ──
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetDone, setResetDone] = useState(false);

    // Shared
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // ── Handle "forgot password" submission ──
    const handleForgotSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) {
            setError('Please enter your email address');
            return;
        }

        setIsLoading(true);
        try {
            await forgotPassword(email.trim());
            setRequestSent(true);
        } catch (err: any) {
            setError(getApiError(err, 'Something went wrong. Please try again.'));
        } finally {
            setIsLoading(false);
        }
    };

    // ── Handle "reset password" submission ──
    const handleResetSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 12) {
            setError('Password must be at least 12 characters long');
            return;
        }

        setIsLoading(true);
        try {
            await resetPassword(token!, newPassword);
            setResetDone(true);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to reset password. The link may have expired.'));
        } finally {
            setIsLoading(false);
        }
    };

    // ── Render: reset completed ──
    if (resetDone) {
        return (
            <div className="auth-page">
                <GlassCard variant="strong" padding="lg" className="auth-container">
                    <div className="auth-header">
                        <h1>AiMe</h1>
                        <h2>Password Reset</h2>
                    </div>
                    <div className="auth-success">
                        Your password has been reset successfully.
                    </div>
                    <div className="auth-footer">
                        <p>
                            <Link to="/login">Sign in with your new password</Link>
                        </p>
                    </div>
                </GlassCard>
            </div>
        );
    }

    // ── Render: forgot-password email sent ──
    if (!token && requestSent) {
        return (
            <div className="auth-page">
                <GlassCard variant="strong" padding="lg" className="auth-container">
                    <div className="auth-header">
                        <h1>AiMe</h1>
                        <h2>Check Your Email</h2>
                    </div>
                    <p style={{ textAlign: 'center', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                        If an account with that email exists, we've sent a password reset link.
                        Please check your inbox (and spam folder).
                    </p>
                    <div className="auth-footer">
                        <p>
                            <Link to="/login">Back to Sign In</Link>
                        </p>
                    </div>
                </GlassCard>
            </div>
        );
    }

    // ── Render: forgot-password form (no token) ──
    if (!token) {
        return (
            <div className="auth-page">
                <GlassCard variant="strong" padding="lg" className="auth-container">
                    <div className="auth-header">
                        <h1>AiMe</h1>
                        <h2>Forgot Password</h2>
                    </div>

                    <form onSubmit={handleForgotSubmit} className="auth-form">
                        {error && <div className="auth-error">{error}</div>}

                        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', lineHeight: 1.6 }}>
                            Enter the email address associated with your account and we'll send you
                            a link to reset your password.
                        </p>

                        <GlassInput
                            label="Email"
                            type="email"
                            value={email}
                            onChange={setEmail}
                            placeholder="Enter your email"
                            autoFocus
                            disabled={isLoading}
                        />

                        <GlassButton
                            type="submit"
                            variant="primary"
                            size="lg"
                            loading={isLoading}
                            disabled={isLoading}
                            className="auth-submit-btn"
                        >
                            Send Reset Link
                        </GlassButton>
                    </form>

                    <div className="auth-footer">
                        <p>
                            Remember your password?{' '}
                            <Link to="/login">Sign in</Link>
                        </p>
                    </div>
                </GlassCard>
            </div>
        );
    }

    // ── Render: new password form (token present) ──
    return (
        <div className="auth-page">
            <GlassCard variant="strong" padding="lg" className="auth-container">
                <div className="auth-header">
                    <h1>AiMe</h1>
                    <h2>Set New Password</h2>
                </div>

                <form onSubmit={handleResetSubmit} className="auth-form">
                    {error && <div className="auth-error">{error}</div>}

                    <GlassInput
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={setNewPassword}
                        placeholder="At least 12 characters"
                        autoFocus
                        disabled={isLoading}
                    />

                    <GlassInput
                        label="Confirm New Password"
                        type="password"
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                        placeholder="Confirm your new password"
                        disabled={isLoading}
                    />

                    <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', lineHeight: 1.5 }}>
                        Must be at least 12 characters with uppercase, lowercase, digit,
                        and special character.
                    </p>

                    <GlassButton
                        type="submit"
                        variant="primary"
                        size="lg"
                        loading={isLoading}
                        disabled={isLoading}
                        className="auth-submit-btn"
                    >
                        Reset Password
                    </GlassButton>
                </form>

                <div className="auth-footer">
                    <p>
                        <Link to="/login">Back to Sign In</Link>
                    </p>
                </div>
            </GlassCard>
        </div>
    );
};

export default ResetPassword;
